import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Basket, BasketItem } from '@reward-platform/order-schemas/basket'
import { getIsItemExpired } from '@reward-platform/order-schemas/util'
import { removeItemFromBasket } from '~/services/basketService'
import { useAuthContext } from '~/context/auth'
import { useBasketQuery } from '../useBasket/useBasket'
import { cancelCheckoutValidation, resetCheckoutBasket } from '../useBasket/useCheckoutBasket'

const removeItemFromBasketIfExpired = async (item: BasketItem, beforeRemove: VoidFunction) => {
  const { id } = item
  const isItemExpired = getIsItemExpired(item)
  const removeItem = !!id && isItemExpired
  if (removeItem) {
    beforeRemove()
    await removeItemFromBasket(id)
  }

  return { wasItemRemoved: Boolean(removeItem) }
}

type OnBasketUpdatedParams = {
  basket: Basket | undefined
  invalidateBasket: () => Promise<void>
  beforeRemove: VoidFunction
}

const onBasketUpdated = async ({
  basket,
  invalidateBasket,
  beforeRemove,
}: OnBasketUpdatedParams) => {
  if (!basket) {
    return
  }

  const allBasketItems = [
    ...basket.items.HOTEL,
    ...basket.items.CAR_HIRE,
    ...basket.items.EXPERIENCE,
    ...basket.items.FLIGHT,
  ]

  const results = await Promise.all(
    allBasketItems.map((item) => removeItemFromBasketIfExpired(item, beforeRemove))
  )

  // If any item was removed we should invalidate the basket
  if (results.some(({ wasItemRemoved }) => wasItemRemoved)) {
    await invalidateBasket()
  }
}

export const useRemoveExpiredBasketItems = () => {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuthContext()
  const basket = useBasketQuery({
    isEnabled: !!isAuthenticated,
  })
  const previousDataUpdatedAtRef = useRef<number>(undefined)

  const { data, dataUpdatedAt, refetch } = basket

  useEffect(() => {
    const isBasketUpdated = previousDataUpdatedAtRef.current !== dataUpdatedAt && data
    previousDataUpdatedAtRef.current = dataUpdatedAt
    if (isBasketUpdated) {
      onBasketUpdated({
        basket: data,
        beforeRemove: () => cancelCheckoutValidation(queryClient),
        invalidateBasket: async () => {
          resetCheckoutBasket(queryClient)
          await refetch()
        },
      })
    }
  }, [data, dataUpdatedAt, queryClient, refetch])
}
